/* eslint-disable */
// @ts-nocheck
// This file is excluded from babel-metadata-plugin due to complex expressions
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Percent,
  Calendar,
  Users,
  AlertTriangle,
  Clock,
  ChevronRight,
  BarChart3,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCcw,
  Filter,
  CheckSquare,
  Phone,
  Info,
  Eye
} from 'lucide-react';
import { useNavigate, Navigate } from 'react-router-dom';
import { InstallmentPerformanceChart } from '../components/ExecutiveCharts';
import { usePermissions } from '../context/PermissionsContext';
import { logger } from '../utils/logger';
import { ChartSettingsButton, getChartSettings } from '../components/ChartSettings';
import DashboardVisibilityPanel from './DashboardVisibilityPanel';
import { fmtKPI } from '../utils/kpiFormat';
import { useKPIDefinition } from '../components/KPIDefinitionModal';
import ActivityTrendsCharts from '../components/ActivityTrendsCharts';
import LeadsByStateMap from '../components/LeadsByStateMap';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Modern KPI Tile Component - Executive Analytics Style
const KPICard = ({ title, value, subValue, icon: Icon, trend, trendValue, onClick, onInfo, variant = 'default', loading }) => {
  const variantStyles = {
    default: {
      iconBg: 'bg-slate-100',
      iconColor: 'text-slate-600'
    },
    primary: {
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600'
    },
    success: {
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-600'
    },
    warning: {
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-600'
    },
    danger: {
      iconBg: 'bg-red-50',
      iconColor: 'text-red-600'
    }
  };

  const style = variantStyles[variant] || variantStyles.default;

  // Display loading state
  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 min-h-[120px] flex flex-col">
        <div className="flex items-start justify-between mb-3">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-8 w-8 rounded-xl flex-shrink-0" />
        </div>
        <Skeleton className="h-7 w-20 mb-2" />
        <Skeleton className="h-3 w-14 mt-auto" />
      </div>
    );
  }

  // Format display value - handle null/undefined
  const displayValue = value === null || value === undefined || value === '-' ? '0' : value;

  return (
    <div 
      className="bg-white rounded-2xl p-5 cursor-pointer transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 border border-slate-100 min-h-[120px] flex flex-col"
      onClick={onClick}
      data-testid={`kpi-${title?.toLowerCase().replace(/\s+/g, '-') || 'kpi'}`}
    >
      {/* Header Row: Title + Icon - Fixed alignment */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide leading-tight flex-1 min-w-0">{title}</span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {onInfo && (
            <button
              onClick={(e) => { e.stopPropagation(); onInfo(); }}
              className="p-1 rounded-full text-slate-300 hover:text-blue-600 hover:bg-blue-50 transition-colors"
              aria-label={`How is ${title} calculated?`}
              data-testid={`kpi-info-${title?.toLowerCase().replace(/\s+/g, '-') || 'kpi'}`}
            >
              <Info className="h-3.5 w-3.5" strokeWidth={2} />
            </button>
          )}
          {Icon && (
            <div className={`${style.iconBg} ${style.iconColor} p-2 rounded-xl flex-shrink-0 flex items-center justify-center w-8 h-8`}>
              <Icon className="h-4 w-4" strokeWidth={2} />
            </div>
          )}
        </div>
      </div>
      
      {/* Value Row */}
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-2xl font-bold text-slate-900 tracking-tight leading-none">{displayValue}</span>
        {trend && trendValue && (
          <span className={`flex items-center text-xs font-semibold ${trend === 'up' ? 'text-emerald-600' : 'text-red-500'}`}>
            {trend === 'up' ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
            {trendValue}
          </span>
        )}
      </div>
      
      {/* Sub Value - Push to bottom */}
      {subValue && <span className="text-xs text-slate-400 mt-auto pt-1 block truncate">{subValue}</span>}
    </div>
  );
};

// 12-Month Expected vs Collected Bar Chart with category breakdown
const MonthlyBarChart = ({ data, categoryFilter, barExpectedColor, barCollectedColor }) => {
  if (!data || data.length === 0) return <div className="h-40 flex items-center justify-center text-slate-400">No data</div>;

  let maxVal = 1;
  for (const m of data) {
    if (m.expected.total > maxVal) maxVal = m.expected.total;
    if (m.collected.total > maxVal) maxVal = m.collected.total;
  }

  const formatK = (v) => {
    if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
    if (v >= 1000) return `$${(v / 1000).toFixed(0)}K`;
    return `$${v.toLocaleString()}`;
  };

  return (
    <div className="px-4 pb-2">
      <div className="flex items-end gap-1.5 h-44">
        {data.map((m) => {
          const expH = maxVal > 0 ? (m.expected.total / maxVal) * 100 : 0;
          const colH = maxVal > 0 ? (m.collected.total / maxVal) * 100 : 0;
          const expLabel = m.expected.total > 0 ? formatK(m.expected.total) : '';
          const colLabel = m.collected.total > 0 ? formatK(m.collected.total) : '';
          return (
            <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full flex gap-0.5 items-end h-32 relative">
                <div
                  className="flex-1 transition-all duration-300 rounded-t hover:opacity-80 relative"
                  style={{ height: `${Math.max(expH, 3)}%`, backgroundColor: barExpectedColor || '#cbd5e1' }}
                  title={`Expected: ${formatK(m.expected.total)}`}
                >
                  {expLabel && (
                    <span className="absolute left-1/2 -translate-x-1/2 -top-4 text-[9px] font-semibold text-slate-600 whitespace-nowrap">
                      {expLabel}
                    </span>
                  )}
                </div>
                <div
                  className="flex-1 transition-all duration-300 rounded-t hover:opacity-80 relative"
                  style={{ height: `${Math.max(colH, 3)}%`, backgroundColor: barCollectedColor || '#10b981' }}
                  title={`Collected: ${formatK(m.collected.total)}`}
                >
                  {colLabel && (
                    <span className="absolute left-1/2 -translate-x-1/2 -top-4 text-[9px] font-semibold text-emerald-700 whitespace-nowrap">
                      {colLabel}
                    </span>
                  )}
                </div>
              </div>
              <span className="text-[10px] font-medium text-slate-400">{m.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Modern Aging Bucket Visual - Horizontal stacked bar with legend
const AgingBucketChart = ({ buckets, onBucketClick }) => {
  const calculateTotal = () => {
    let sum = 0;
    const values = Object.values(buckets);
    for (const b of values) {
      sum += b.amount || 0;
    }
    return sum || 1;
  };
  const total = calculateTotal();
  
  const colors = {
    '0_30': { bar: 'bg-emerald-500', dot: 'bg-emerald-500', hover: 'hover:bg-emerald-600' },
    '31_60': { bar: 'bg-amber-400', dot: 'bg-amber-400', hover: 'hover:bg-amber-500' },
    '61_90': { bar: 'bg-orange-500', dot: 'bg-orange-500', hover: 'hover:bg-orange-600' },
    '90_plus': { bar: 'bg-red-500', dot: 'bg-red-500', hover: 'hover:bg-red-600' }
  };
  
  const bucketEntries = Object.entries(buckets);

  return (
    <div className="p-4 space-y-4">
      {/* Progress Bar */}
      <div className="flex h-4 rounded-full overflow-hidden bg-slate-100">
        {bucketEntries.map(([key, bucket]) => {
          const widthPercent = (bucket.amount / total) * 100;
          const color = colors[key];
          const bucketTitle = `${bucket.label}: $${(bucket.amount || 0).toLocaleString()} (${bucket.count} installments)`;
          
          if (widthPercent < 1) return null;
          
          return (
            <div
              key={key}
              className={`${color.bar} ${color.hover} cursor-pointer transition-all duration-200 first:rounded-l-full last:rounded-r-full`}
              style={{ width: `${widthPercent}%` }}
              onClick={() => onBucketClick(key)}
              title={bucketTitle}
            />
          );
        })}
      </div>
      
      {/* Legend Grid */}
      <div className="grid grid-cols-2 gap-3">
        {bucketEntries.map(([key, bucket]) => {
          const amountFormatted = `$${(bucket.amount || 0).toLocaleString()}`;
          const color = colors[key];
          
          return (
            <button
              key={key}
              className="text-left p-3 rounded-xl hover:bg-slate-50 transition-all duration-200 border border-transparent hover:border-slate-200"
              onClick={() => onBucketClick(key)}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <div className={`w-2.5 h-2.5 rounded-full ${color.dot}`} />
                <span className="text-xs font-medium text-slate-500">{bucket.label}</span>
              </div>
              <div className="font-bold text-slate-900 text-lg">{amountFormatted}</div>
              <div className="text-xs text-slate-400">{bucket.count} installments</div>
            </button>
          );
        })}
      </div>
    </div>
  );
};


const CHART_COLORS = ['#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#06b6d4', '#14b8a6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#64748b', '#84cc16'];

const StageDistributionChart = ({ data }) => {
  const [hovered, setHovered] = useState(null);
  const stages = data.all_stages.filter(s => s.count > 0);
  const total = stages.reduce((sum, s) => sum + s.count, 0);

  if (total === 0) return <div className="h-64 flex items-center justify-center text-slate-400">No data</div>;

  // Build path data
  let cumAngle = 0;
  const slices = stages.map((s, i) => {
    const angle = (s.count / total) * 360;
    const startAngle = cumAngle;
    cumAngle += angle;
    const midAngle = startAngle + angle / 2;
    const startRad = (startAngle - 90) * Math.PI / 180;
    const endRad = (cumAngle - 90) * Math.PI / 180;
    const midRad = (midAngle - 90) * Math.PI / 180;
    const largeArc = angle > 180 ? 1 : 0;
    const r = 90;
    const x1 = 100 + r * Math.cos(startRad);
    const y1 = 100 + r * Math.sin(startRad);
    const x2 = 100 + r * Math.cos(endRad);
    const y2 = 100 + r * Math.sin(endRad);
    const path = stages.length === 1
      ? `M 100 100 m -${r} 0 a ${r} ${r} 0 1 1 ${r*2} 0 a ${r} ${r} 0 1 1 -${r*2} 0`
      : `M 100 100 L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
    // Tooltip anchor point
    const tipR = 65;
    const tipX = 100 + tipR * Math.cos(midRad);
    const tipY = 100 + tipR * Math.sin(midRad);
    return { ...s, path, color: CHART_COLORS[i % CHART_COLORS.length], tipX, tipY, idx: i };
  });

  return (
    <div className="flex flex-col sm:flex-row items-center gap-4 min-h-[280px]">
      {/* Legend on the left */}
      <div className="flex flex-col gap-1.5 w-full sm:w-[45%] sm:flex-shrink-0">
        {slices.map(s => (
          <div
            key={s.stage_id}
            className={`flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all cursor-default ${
              hovered === s.idx ? 'bg-slate-100 scale-[1.02]' : ''
            }`}
            onMouseEnter={() => setHovered(s.idx)}
            onMouseLeave={() => setHovered(null)}
          >
            <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: s.color }} />
            <span className="text-sm text-slate-700 truncate flex-1">{s.label}</span>
            <span className="text-sm font-semibold text-slate-900 tabular-nums">{s.count}</span>
            <span className="text-xs text-slate-400 w-10 text-right">{s.percentage}%</span>
          </div>
        ))}
      </div>

      {/* Donut Chart on the right */}
      <div className="flex-1 flex items-center justify-center relative">
        <svg viewBox="0 0 200 200" className="w-full max-w-[260px] h-auto">
          {slices.map(s => (
            <path
              key={s.stage_id}
              d={s.path}
              fill={s.color}
              opacity={hovered !== null && hovered !== s.idx ? 0.4 : 1}
              className="transition-all duration-200 cursor-pointer"
              style={{ transform: hovered === s.idx ? `translate(${(s.tipX - 100) * 0.04}px, ${(s.tipY - 100) * 0.04}px)` : 'none' }}
              onMouseEnter={() => setHovered(s.idx)}
              onMouseLeave={() => setHovered(null)}
            />
          ))}
          <circle cx="100" cy="100" r="50" fill="white" />
          <text x="100" y="95" textAnchor="middle" style={{ fontSize: '22px', fontWeight: 800 }} className="fill-slate-900">{data.total_leads}</text>
          <text x="100" y="112" textAnchor="middle" style={{ fontSize: '10px' }} className="fill-slate-400">total leads</text>
        </svg>
        {/* Hover tooltip */}
        {hovered !== null && slices[hovered] && (
          <div
            className="absolute pointer-events-none bg-slate-900 text-white px-3 py-1.5 rounded-lg shadow-lg text-xs whitespace-nowrap z-10"
            style={{ top: '8px', right: '8px' }}
          >
            <span className="font-semibold">{slices[hovered].label}</span>
            <span className="mx-1.5 text-slate-400">|</span>
            <span>{slices[hovered].count} leads</span>
            <span className="mx-1.5 text-slate-400">|</span>
            <span>{slices[hovered].percentage}%</span>
          </div>
        )}
      </div>
    </div>
  );
};


export default function IntelligencePage() {
  const { role, loading: permLoading } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [visibilityOpen, setVisibilityOpen] = useState(false);
  const [incomeKPIs, setIncomeKPIs] = useState(null);
  const [financialData, setFinancialData] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [chartYear, setChartYear] = useState(new Date().getFullYear());
  const [chartCategory, setChartCategory] = useState('all');
  const [funnelData, setFunnelData] = useState(null);
  const [funnelKPIs, setFunnelKPIs] = useState(null);
  const [funnelRange, setFunnelRange] = useState('all'); // all | day | week | month
  const [funnelDate, setFunnelDate] = useState(new Date().toISOString().split('T')[0]);
  const [stageDistribution, setStageDistribution] = useState(null);
  const [eventData, setEventData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('this_month');
  const [selectedRep, setSelectedRep] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [reps, setReps] = useState([]);
  
  // Modal states
  const [showCloseRateModal, setShowCloseRateModal] = useState(false);
  const [showOverdueModal, setShowOverdueModal] = useState(false);
  const [showStuckLeadsModal, setShowStuckLeadsModal] = useState(false);
  const [showFunnelModal, setShowFunnelModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [overdueWorklist, setOverdueWorklist] = useState([]);
  const [stuckLeads, setStuckLeads] = useState([]);
  const [selectedBucket, setSelectedBucket] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null);
  const [executionData, setExecutionData] = useState(null);
  const [execRep, setExecRep] = useState('all');
  const [widgetVisibility, setWidgetVisibility] = useState({});
  const [chartSettings, setChartSettings] = useState(getChartSettings);
  const { openKPI, modal: kpiModal } = useKPIDefinition();
  
  const navigate = useNavigate();

  // Redirect AE users to dashboard - they shouldn't access Intelligence page
  if (!permLoading && role === 'account_executive') {
    return <Navigate to="/dashboard" replace />;
  }

  const fetchIncomeKPIs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('month', selectedMonth);
      if (selectedRep !== 'all') params.append('rep_id', selectedRep);
      
      const response = await fetch(
        `${API_URL}/api/intelligence/income-kpis?${params}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setIncomeKPIs(data);
      }
    } catch (error) {
      logger.error('Error fetching income KPIs:', error);
    }
  }, [selectedRep, selectedMonth]);

  const fetchFinancialData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedRep !== 'all') params.append('rep_id', selectedRep);
      
      const response = await fetch(
        `${API_URL}/api/intelligence/financial${params.toString() ? `?${params}` : ''}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setFinancialData(data);
      }
    } catch (error) {
      logger.error('Error fetching financial data:', error);
    }
  }, [selectedRep]);

  const fetchChartData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('year', chartYear);
      if (chartCategory !== 'all') params.append('category', chartCategory);
      if (selectedRep !== 'all') params.append('rep_id', selectedRep);
      
      const response = await fetch(
        `${API_URL}/api/intelligence/expected-vs-collected?${params}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setChartData(data);
      }
    } catch (error) {
      logger.error('Error fetching chart data:', error);
    }
  }, [selectedRep, chartYear, chartCategory]);

  const fetchFunnelData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedRep !== 'all') params.append('rep_id', selectedRep);
      
      const response = await fetch(
        `${API_URL}/api/intelligence/funnel${params.toString() ? `?${params}` : ''}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setFunnelData(data);
      }
    } catch (error) {
      logger.error('Error fetching funnel data:', error);
    }
  }, [selectedRep]);

  const fetchFunnelKPIs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedRep !== 'all') params.append('rep_id', selectedRep);
      // Compute date range based on funnelRange + funnelDate
      if (funnelRange !== 'all') {
        const base = new Date(funnelDate + 'T00:00:00Z');
        let start, end;
        if (funnelRange === 'day') {
          start = new Date(base); end = new Date(base);
          end.setUTCHours(23, 59, 59);
        } else if (funnelRange === 'week') {
          const dow = base.getUTCDay();
          start = new Date(base); start.setUTCDate(start.getUTCDate() - dow);
          end = new Date(start); end.setUTCDate(end.getUTCDate() + 6);
          end.setUTCHours(23, 59, 59);
        } else if (funnelRange === 'month') {
          start = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), 1));
          end = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + 1, 0, 23, 59, 59));
        }
        if (start) params.append('start_date', start.toISOString());
        if (end) params.append('end_date', end.toISOString());
      }
      const response = await fetch(
        `${API_URL}/api/intelligence/funnel-kpis?${params}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        setFunnelKPIs(await response.json());
      }
    } catch (error) {
      logger.error('Error fetching funnel KPIs:', error);
    }
  }, [selectedRep, funnelRange, funnelDate]);

  const fetchStageDistribution = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedRep !== 'all') params.append('rep_id', selectedRep);
      const response = await fetch(
        `${API_URL}/api/intelligence/stage-distribution?${params}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        setStageDistribution(await response.json());
      }
    } catch (error) {
      logger.error('Error fetching stage distribution:', error);
    }
  }, [selectedRep]);

  const fetchEventData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedRep !== 'all') params.append('rep_id', selectedRep);
      
      const response = await fetch(
        `${API_URL}/api/intelligence/events${params.toString() ? `?${params}` : ''}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setEventData(data);
      }
    } catch (error) {
      logger.error('Error fetching event data:', error);
    }
  }, [selectedRep]);

  const fetchExecutionData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (execRep !== 'all') params.append('rep_id', execRep);
      
      const response = await fetch(
        `${API_URL}/api/intelligence/execution${params.toString() ? `?${params}` : ''}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setExecutionData(data);
      }
    } catch (error) {
      logger.error('Error fetching execution data:', error);
    }
  }, [execRep]);

  const fetchReps = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setReps(data.filter(u => ['sales_rep', 'sales_manager', 'admin', 'owner'].includes(u.role)));
      }
    } catch (error) {
      logger.error('Error fetching reps:', error);
    }
  };

  const fetchVisibility = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/dashboard-visibility/my`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setWidgetVisibility(d.widgets || {});
      }
    } catch { /* silent */ }
  }, []);

  const fetchOverdueWorklist = async (bucket = null) => {
    try {
      const params = new URLSearchParams();
      if (bucket) params.append('bucket', bucket);
      if (selectedRep !== 'all') params.append('rep_id', selectedRep);
      
      const response = await fetch(
        `${API_URL}/api/intelligence/overdue-worklist${params.toString() ? `?${params}` : ''}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setOverdueWorklist(data.worklist || []);
        setSelectedBucket(bucket);
        setShowOverdueModal(true);
      }
    } catch (error) {
      logger.error('Error fetching overdue worklist:', error);
    }
  };

  const fetchStuckLeads = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedRep !== 'all') params.append('rep_id', selectedRep);
      
      const response = await fetch(
        `${API_URL}/api/intelligence/stuck-leads${params.toString() ? `?${params}` : ''}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setStuckLeads(data.stuck_leads || []);
        setShowStuckLeadsModal(true);
      }
    } catch (error) {
      logger.error('Error fetching stuck leads:', error);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await Promise.all([fetchIncomeKPIs(), fetchChartData(), fetchFinancialData(), fetchFunnelData(), fetchFunnelKPIs(), fetchStageDistribution(), fetchEventData(), fetchExecutionData()]);
    setRefreshing(false);
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchReps(), fetchVisibility(), fetchIncomeKPIs(), fetchChartData(), fetchFinancialData(), fetchFunnelData(), fetchFunnelKPIs(), fetchStageDistribution(), fetchEventData(), fetchExecutionData()]);
      setLoading(false);
    };
    loadData();
  }, [fetchIncomeKPIs, fetchChartData, fetchFinancialData, fetchFunnelData, fetchFunnelKPIs, fetchStageDistribution, fetchEventData, fetchExecutionData, fetchVisibility]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatCurrency = fmtKPI;

  const vis = (wid) => widgetVisibility[wid] !== false;

  // Generate month options for the year
  const monthOptions = (() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const months = [];
    for (let m = 0; m < 12; m++) {
      const val = `${currentYear}-${String(m + 1).padStart(2, '0')}`;
      const label = new Date(currentYear, m, 1).toLocaleString('en-US', { month: 'short', year: 'numeric' });
      months.push({ value: val, label });
    }
    return months;
  })();

  return (
    <div className="space-y-6 p-4 md:p-6 bg-[#f8fafc] min-h-screen" data-testid="intelligence-dashboard">
      {kpiModal}
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Intelligence Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Sales & Collections Operating System</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-36 sm:w-44 h-10 bg-white border-slate-200 rounded-xl" data-testid="month-selector">
              <Calendar className="h-4 w-4 mr-2 text-slate-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map((m) => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedRep} onValueChange={setSelectedRep}>
            <SelectTrigger className="w-36 sm:w-48 h-10 bg-white border-slate-200 rounded-xl" data-testid="rep-filter">
              <Filter className="h-4 w-4 mr-2 text-slate-400" />
              <SelectValue placeholder="All Reps" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Reps</SelectItem>
              {reps.map((rep) => (
                <SelectItem key={rep.user_id} value={rep.user_id}>{rep.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ChartSettingsButton onSettingsChange={setChartSettings} />
          {(role === 'owner' || role === 'admin') && (
            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10 bg-white border-slate-200 rounded-xl hover:bg-slate-50 hover:border-slate-300 text-slate-700"
              onClick={() => setVisibilityOpen(true)}
              data-testid="intel-widget-visibility-btn"
              title="Widget Visibility"
            >
              <Eye className="h-4 w-4" />
            </Button>
          )}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={refreshData}
            disabled={refreshing}
            className="h-10 px-4 bg-white border-slate-200 rounded-xl hover:bg-slate-50 hover:border-slate-300 text-slate-700"
            data-testid="refresh-btn"
          >
            <RefreshCcw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Income Intelligence KPI Strip */}
      {vis('intel_income_kpis') && (
      <div>
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
          Income Intelligence {incomeKPIs ? `\u2014 ${incomeKPIs.month_label}` : ''}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
          <KPICard
            title="Total Sales Value"
            value={incomeKPIs ? formatCurrency(incomeKPIs.total_sales_value) : '-'}
            subValue="Net from contracts"
            icon={DollarSign}
            variant="primary"
            loading={loading}
            onInfo={() => openKPI({
              title: 'Total Sales Value',
              value: incomeKPIs ? formatCurrency(incomeKPIs.total_sales_value) : '-',
              formula: 'SUM(financial_contracts.net_price) WHERE contract NOT cancelled',
              description: 'All-time net contract value across every active/completed/overdue contract in the CRM. Includes all contract types (installment, one-time, etc.).',
              source: 'financial_contracts collection',
            })}
            data-testid="kpi-total-sales"
          />
          <KPICard
            title="Collected Till Date"
            value={incomeKPIs ? formatCurrency(incomeKPIs.collected_till_date) : '-'}
            subValue="All-time payments"
            icon={CheckSquare}
            variant="success"
            loading={loading}
            onInfo={() => openKPI({
              title: 'Collected Till Date',
              value: incomeKPIs ? formatCurrency(incomeKPIs.collected_till_date) : '-',
              formula: 'SUM(payments.amount) WHERE voided = false',
              description: 'Total payments received across all time and all categories (Booking Money, Down Payment, Installment, Future Adjustment, One-time). Excludes voided payments.',
              source: 'payments collection',
            })}
            data-testid="kpi-collected-till-date"
          />
          <KPICard
            title="Expected This Month"
            value={incomeKPIs ? formatCurrency(incomeKPIs.expected_this_month) : '-'}
            subValue="All receivables"
            icon={Calendar}
            variant="default"
            loading={loading}
            onInfo={() => openKPI({
              title: 'Expected This Month',
              value: incomeKPIs ? formatCurrency(incomeKPIs.expected_this_month) : '-',
              formula: 'SUM(installments due in month) + SUM(booking_money due in month) + SUM(downpayment due in month) + SUM(future_adjustments in month) + SUM(one_time contracts due in month)',
              description: 'Total expected receivables for the selected month across every payment category — including one-time contract settlements.',
              source: 'Installments + Financial contracts + Future Adjustments',
            })}
            data-testid="kpi-expected-month"
          />
          <KPICard
            title="Collected This Month"
            value={incomeKPIs ? formatCurrency(incomeKPIs.collected_this_month) : '-'}
            subValue="All payments"
            icon={TrendingUp}
            variant="success"
            loading={loading}
            onInfo={() => openKPI({
              title: 'Collected This Month',
              value: incomeKPIs ? formatCurrency(incomeKPIs.collected_this_month) : '-',
              formula: 'SUM(payments.amount) WHERE payment_date ∈ month AND voided = false',
              description: 'All payments (every category, including one-time) received with a payment_date in the selected month.',
              source: 'payments collection',
            })}
            data-testid="kpi-collected-month"
          />
          <KPICard
            title="Receivable Outstanding"
            value={incomeKPIs ? formatCurrency(incomeKPIs.receivable_outstanding_month) : '-'}
            subValue="Not yet received"
            icon={AlertTriangle}
            variant={incomeKPIs && incomeKPIs.receivable_outstanding_month > 0 ? 'warning' : 'default'}
            loading={loading}
            onInfo={() => openKPI({
              title: 'Receivable Outstanding',
              value: incomeKPIs ? formatCurrency(incomeKPIs.receivable_outstanding_month) : '-',
              formula: 'Expected This Month − Collected This Month (floored at 0)',
              description: 'Gap between what was scheduled to arrive this month and what has actually been collected so far.',
              source: 'Derived from the two KPIs above',
            })}
            data-testid="kpi-outstanding"
          />
          <KPICard
            title="Collection Rate"
            value={incomeKPIs ? `${incomeKPIs.collection_rate}%` : '-'}
            subValue={incomeKPIs ? `${formatCurrency(incomeKPIs.collected_this_month)} / ${formatCurrency(incomeKPIs.expected_this_month)}` : ''}
            icon={Percent}
            variant={incomeKPIs ? (incomeKPIs.collection_rate >= 80 ? 'success' : incomeKPIs.collection_rate >= 50 ? 'warning' : 'danger') : 'default'}
            loading={loading}
            onInfo={() => openKPI({
              title: 'Collection Rate',
              value: incomeKPIs ? `${incomeKPIs.collection_rate}%` : '-',
              formula: '(Collected This Month ÷ Expected This Month) × 100',
              description: 'Percentage of this month\'s expected receivables that have actually been collected. ≥80% is healthy (green), 50–79% is amber, below 50% is red.',
              source: 'Derived',
            })}
            data-testid="kpi-collection-rate"
          />
          <KPICard
            title="Installments (Month)"
            value={incomeKPIs ? formatCurrency(incomeKPIs.installments_this_month) : '-'}
            subValue="Scheduled installments"
            icon={BarChart3}
            variant="primary"
            loading={loading}
            onInfo={() => openKPI({
              title: 'Installments This Month',
              value: incomeKPIs ? formatCurrency(incomeKPIs.installments_this_month) : '-',
              formula: 'SUM(installments.due_amount) WHERE installment.due_date ∈ month',
              description: 'Only the installment portion due this month. Excludes booking money, down payments, future adjustments, and one-time contracts.',
              source: 'installments collection',
            })}
            data-testid="kpi-installments-month"
          />
          <KPICard
            title="Collected Installments"
            value={incomeKPIs ? formatCurrency(incomeKPIs.collected_installments) : '-'}
            subValue="Installment payments"
            icon={CheckSquare}
            variant="success"
            loading={loading}
            onInfo={() => openKPI({
              title: 'Collected Installments',
              value: incomeKPIs ? formatCurrency(incomeKPIs.collected_installments) : '-',
              formula: 'SUM(payments.amount) WHERE payment_category ∈ {"Installment", "Partial", "Full-Payment"} AND payment_date ∈ month',
              description: 'Installment-only payments received this month. Does not include one-time, booking money, down payment, or future adjustments.',
              source: 'payments collection',
            })}
            data-testid="kpi-collected-installments"
          />
        </div>
      </div>
      )}

      {/* ROW 2-4: Financial Intelligence */}
      {vis('intel_expected_collected') && (<>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Expected vs Collected 12-Month Chart */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-soft overflow-hidden">
          <div className="px-4 md:px-6 pt-6 pb-2 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-slate-400" />
                Expected vs Collected
              </h3>
              <p className="text-xs text-slate-500 mt-1">12-month view with category breakdown</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={chartCategory} onValueChange={setChartCategory}>
                <SelectTrigger className="w-36 h-8 text-xs" data-testid="chart-category-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="installment">Installment</SelectItem>
                  <SelectItem value="booking_money">Booking Money</SelectItem>
                  <SelectItem value="downpayment">Down Payment</SelectItem>
                  <SelectItem value="future_adjustment">Future Adj.</SelectItem>
                  <SelectItem value="one_time">One-Time</SelectItem>
                </SelectContent>
              </Select>
              <Select value={String(chartYear)} onValueChange={(v) => setChartYear(Number(v))}>
                <SelectTrigger className="w-24 h-8 text-xs" data-testid="chart-year-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map(y => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="px-2">
            {loading ? (
              <Skeleton className="h-40 w-full mx-4 mb-4" />
            ) : chartData?.months ? (
              <>
                <MonthlyBarChart data={chartData.months} categoryFilter={chartCategory} barExpectedColor={chartSettings.barExpectedColor} barCollectedColor={chartSettings.barCollectedColor} />
                <div className="flex items-center gap-4 md:gap-6 px-4 pb-5 text-sm flex-wrap">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 bg-slate-200 rounded-full" />
                    <span className="text-slate-500 text-xs">Expected</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full" />
                    <span className="text-slate-500 text-xs">Collected</span>
                  </div>
                  <div className="ml-auto text-slate-500 text-xs">
                    {chartCategory !== 'all' && <Badge variant="outline" className="mr-2 text-xs">{chartCategory.replace('_', ' ')}</Badge>}
                    Year: <span className="font-bold text-slate-900">{chartYear}</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="h-40 flex items-center justify-center text-slate-400">No data available</div>
            )}
          </div>
        </div>

        {/* Aging Buckets */}
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          <div className="px-6 pt-6 pb-2">
            <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <PieChart className="h-5 w-5 text-slate-400" />
              Aging Buckets
            </h3>
            <p className="text-xs text-slate-500 mt-1">Outstanding by age</p>
          </div>
          <div>
            {loading ? (
              <Skeleton className="h-40 w-full mx-4 mb-4" />
            ) : financialData?.aging_buckets ? (
              <AgingBucketChart 
                buckets={financialData.aging_buckets} 
                onBucketClick={fetchOverdueWorklist}
              />
            ) : (
              <div className="h-40 flex items-center justify-center text-slate-400">No data available</div>
            )}
          </div>
        </div>
      </div>

      {/* Installment Performance Chart — DISABLED per user request (Feb 2026)
          Data was not matching what the user expected; the Income Intelligence
          KPIs and Expected vs Collected stacked-bar above already cover this.
          Re-enable by uncommenting the line below once the data discrepancy is
          investigated.
      <InstallmentPerformanceChart /> */}

      {/* Activity Trends (Appointments vs Consultations · Calls vs Opportunities) */}
      <ActivityTrendsCharts reps={reps} />

      {/* Top Delinquent Customers */}
      <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
        <div className="px-4 md:px-6 py-5 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Top 20 Delinquent Customers
            </h3>
            <p className="text-xs text-slate-500 mt-1">Highest outstanding balances</p>
          </div>
          <span className="px-3 py-1 bg-red-50 text-red-600 text-xs font-semibold rounded-full">
            {financialData?.top_delinquent_customers?.length || 0} accounts
          </span>
        </div>
        <div className="p-0">
          {loading ? (
            <Skeleton className="h-64 w-full m-6" />
          ) : financialData?.top_delinquent_customers?.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Rep</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Overdue Amount</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Overdue #</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Oldest Due</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Last Payment</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Next Due</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {financialData.top_delinquent_customers.slice(0, 10).map((customer) => {
                    const overdueAmountStr = `$${(customer.overdue_amount || 0).toLocaleString()}`;
                    const oldestDueStr = customer.oldest_due_date ? new Date(customer.oldest_due_date).toLocaleDateString() : '-';
                    const lastPaymentStr = customer.last_payment_date ? new Date(customer.last_payment_date).toLocaleDateString() : 'Never';
                    const nextDueStr = customer.next_due_date ? new Date(customer.next_due_date).toLocaleDateString() : '-';
                    
                    return (
                      <TableRow key={customer.contract_id} className="hover:bg-slate-50/80 transition-colors">
                        <TableCell className="py-4">
                          <div>
                            <div className="font-semibold text-slate-900">{customer.customer_name}</div>
                            <div className="text-xs text-slate-400 mt-0.5">{customer.customer_phone}</div>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{customer.rep_name}</TableCell>
                        <TableCell className="text-right">
                          <span className="font-bold text-red-600">{overdueAmountStr}</span>
                        </TableCell>
                        <TableCell className="text-right text-slate-600">{customer.overdue_installments}</TableCell>
                        <TableCell className="text-sm text-slate-600">{oldestDueStr}</TableCell>
                        <TableCell className="text-sm text-slate-600">{lastPaymentStr}</TableCell>
                        <TableCell className="text-sm text-slate-600">{nextDueStr}</TableCell>
                        <TableCell>
                          <button 
                            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                            onClick={() => navigate(`/contracts/${customer.contract_id}`)}
                          >
                            <ChevronRight className="h-4 w-4 text-slate-400" />
                          </button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="h-40 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <TrendingUp className="h-10 w-10 mx-auto mb-3 text-emerald-500" />
                <p className="font-medium">No delinquent customers</p>
                <p className="text-sm mt-1">Great collections performance!</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Rep Collection Accountability */}
      <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-100">
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Users className="h-5 w-5 text-slate-400" />
            Rep Collection Accountability
          </h3>
          <p className="text-xs text-slate-500 mt-1">Performance by sales representative</p>
        </div>
        <div className="p-0">
          {loading ? (
            <Skeleton className="h-48 w-full m-6" />
          ) : financialData?.rep_accountability?.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Rep</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Expected (Month)</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Collected (Month)</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Collection %</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Overdue</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Total Receivable</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Delinquency %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {financialData.rep_accountability.map((rep) => {
                    const expectedStr = `$${(rep.expected_this_month || 0).toLocaleString()}`;
                    const collectedStr = `$${(rep.collected_this_month || 0).toLocaleString()}`;
                    const overdueStr = `$${(rep.overdue_amount || 0).toLocaleString()}`;
                    const receivableStr = `$${(rep.total_receivable || 0).toLocaleString()}`;
                    const collectionVariant = rep.collection_rate >= 80 ? 'default' : rep.collection_rate >= 50 ? 'secondary' : 'destructive';
                    const delinquencyVariant = rep.delinquency_rate <= 10 ? 'default' : rep.delinquency_rate <= 25 ? 'secondary' : 'destructive';
                    
                    return (
                      <TableRow key={rep.rep_id} className="hover:bg-slate-50">
                        <TableCell>
                          <div className="font-medium">{rep.rep_name}</div>
                          <div className="text-xs text-slate-500">{rep.contracts_count} contracts</div>
                        </TableCell>
                        <TableCell className="text-right">{expectedStr}</TableCell>
                        <TableCell className="text-right font-medium text-green-600">{collectedStr}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant={collectionVariant}>{rep.collection_rate}%</Badge>
                        </TableCell>
                        <TableCell className="text-right text-red-600">{overdueStr}</TableCell>
                        <TableCell className="text-right">{receivableStr}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant={delinquencyVariant}>{rep.delinquency_rate}%</Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="h-40 flex items-center justify-center text-slate-400">No rep data available</div>
          )}
        </div>
      </div>
      </>)}

      {/* ROW 2: Funnel Intelligence */}
      {vis('intel_funnel_kpis') && (
      <div className="space-y-4 pt-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-blue-500" />
            <h2 className="text-lg font-bold text-slate-900">Funnel Intelligence</h2>
            {funnelKPIs?.date_filtered && (
              <Badge variant="outline" className="text-xs">Filtered</Badge>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex bg-slate-100 rounded-lg p-0.5" data-testid="funnel-range-tabs">
              {[
                { value: 'all', label: 'All Time' },
                { value: 'day', label: 'Day' },
                { value: 'week', label: 'Week' },
                { value: 'month', label: 'Month' },
              ].map(opt => (
                <button
                  key={opt.value}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
                    funnelRange === opt.value ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'
                  }`}
                  onClick={() => setFunnelRange(opt.value)}
                  data-testid={`funnel-range-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {funnelRange !== 'all' && (
              <input
                type="date"
                value={funnelDate}
                onChange={(e) => setFunnelDate(e.target.value)}
                className="h-8 px-2 text-xs border border-slate-200 rounded-lg bg-white"
                data-testid="funnel-date-picker"
              />
            )}
          </div>
        </div>
        
        {/* Funnel KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
          <KPICard
            title="Total Leads"
            value={funnelKPIs ? funnelKPIs.total_leads : '-'}
            icon={Users}
            variant="default"
            loading={loading}
            onClick={() => navigate('/leads')}
            onInfo={() => openKPI({
              title: 'Total Leads',
              value: funnelKPIs ? String(funnelKPIs.total_leads) : '-',
              formula: 'COUNT(leads) WHERE stage ∉ hidden staging stages',
              description: 'All-time (or date-filtered) lead count, excluding leads awaiting admin triage (landing_page_new, field_data_new). In date-filtered mode, counts leads created in range.',
              source: 'leads collection',
            })}
            data-testid="funnel-total-leads"
          />
          <KPICard
            title={funnelKPIs?.stage_labels?.[funnelKPIs?.stage_semantics?.qualified_stage_id] || "Qualified"}
            value={funnelKPIs ? funnelKPIs.qualified : '-'}
            icon={TrendingUp}
            variant="primary"
            loading={loading}
            onClick={() => navigate(`/leads?stage=${funnelKPIs?.stage_semantics?.qualified_stage_id || 'qualified'}`)}
            onInfo={() => openKPI({
              title: funnelKPIs?.stage_labels?.[funnelKPIs?.stage_semantics?.qualified_stage_id] || 'Qualified',
              value: funnelKPIs ? String(funnelKPIs.qualified) : '-',
              formula: `COUNT(leads) WHERE stage = "${funnelKPIs?.stage_semantics?.qualified_stage_id || 'qualified'}"`,
              description: 'Leads currently sitting at the Qualified stage (or date-filtered: leads that moved to Qualified in range). Qualified stage is dynamically derived from stage settings.',
              source: 'leads collection + lead_activities (when filtered)',
            })}
            data-testid="funnel-qualified"
          />
          <KPICard
            title={funnelKPIs?.stage_labels?.[funnelKPIs?.stage_semantics?.consult_done_stage_id] || "Consult Completed"}
            value={funnelKPIs ? funnelKPIs.consultation_completed : '-'}
            icon={Calendar}
            variant="default"
            loading={loading}
            onClick={() => navigate(`/leads?stage=${funnelKPIs?.stage_semantics?.consult_done_stage_id || 'consultation_completed'}`)}
            onInfo={() => openKPI({
              title: funnelKPIs?.stage_labels?.[funnelKPIs?.stage_semantics?.consult_done_stage_id] || 'Consultation Completed',
              value: funnelKPIs ? String(funnelKPIs.consultation_completed) : '-',
              formula: `COUNT(leads) WHERE stage = "${funnelKPIs?.stage_semantics?.consult_done_stage_id || 'consultation_completed'}"`,
              description: 'Leads that have reached the consultation-completed milestone. Stage is dynamically derived from your Stage Settings.',
              source: 'leads collection',
            })}
            data-testid="funnel-consult-completed"
          />
          <KPICard
            title={(funnelKPIs?.stage_semantics?.won_stage_ids || []).map(id => funnelKPIs?.stage_labels?.[id]).filter(Boolean).join(' / ') || "Closed Won"}
            value={funnelKPIs ? funnelKPIs.closed_won : '-'}
            icon={DollarSign}
            variant="success"
            loading={loading}
            onClick={() => {
              const wid = (funnelKPIs?.stage_semantics?.won_stage_ids || [])[0] || 'closed_won';
              navigate(`/leads?stage=${wid}`);
            }}
            onInfo={() => openKPI({
              title: 'Closed Won',
              value: funnelKPIs ? String(funnelKPIs.closed_won) : '-',
              formula: `COUNT(leads) WHERE stage ∈ {${(funnelKPIs?.stage_semantics?.won_stage_ids || []).join(', ')}}`,
              description: 'Leads that have reached a Won terminal stage. Won stages are detected dynamically via the `is_won` flag or fallback `closed_won` stage id.',
              source: 'Stage Settings + leads collection',
            })}
            data-testid="funnel-closed-won"
          />
          <KPICard
            title="In Opportunity"
            value={funnelKPIs ? funnelKPIs.opportunity_leads : '-'}
            subValue={funnelKPIs ? `${funnelKPIs.pipeline_stages?.length || 0} stages` : ''}
            icon={TrendingUp}
            variant="primary"
            loading={loading}
            onInfo={() => openKPI({
              title: 'In Opportunity',
              value: funnelKPIs ? String(funnelKPIs.opportunity_leads) : '-',
              formula: 'COUNT(leads) WHERE stage ∈ opportunity-category stages (incl. terminal)',
              description: 'Total leads in every opportunity-category stage. This INCLUDES terminal stages (Won/Lost) for historical volume; the Pipeline Value KPI below uses only active opportunity stages.',
              inputs: [
                { label: 'Pipeline stages (active only)', value: funnelKPIs?.pipeline_stages?.length || 0 },
                { label: 'All opportunity stages', value: funnelKPIs?.opportunity_stages?.length || 0 },
              ],
              source: 'Stage Settings + leads collection',
            })}
            data-testid="funnel-opportunity"
          />
          <KPICard
            title="Pipeline Value"
            value={funnelKPIs ? formatCurrency(funnelKPIs.pipeline_value) : '-'}
            subValue="Excl. won/lost"
            icon={DollarSign}
            variant="warning"
            loading={loading}
            onInfo={() => openKPI({
              title: 'Pipeline Value',
              value: funnelKPIs ? formatCurrency(funnelKPIs.pipeline_value) : '-',
              formula: 'SUM(leads.deal_value) WHERE stage ∈ active opportunity stages',
              description: 'Total declared deal value sitting in the opportunity pipeline (excludes Won/Lost). Set when a lead is promoted to opportunity.',
              source: 'leads.deal_value',
            })}
            data-testid="funnel-pipeline-value"
          />
          <KPICard
            title="Cash Collection"
            value={funnelKPIs ? formatCurrency(funnelKPIs.cash_collection_value) : '-'}
            subValue="Expected from opp."
            icon={DollarSign}
            variant="primary"
            loading={loading}
            onInfo={() => openKPI({
              title: 'Cash Collection Value',
              value: funnelKPIs ? formatCurrency(funnelKPIs.cash_collection_value) : '-',
              formula: 'SUM(leads.estimated_cash_collection) WHERE stage ∈ active opportunity stages',
              description: 'Expected cash (non-financed portion) across the open opportunity pipeline. Typically less than Pipeline Value when deals include financing.',
              source: 'leads.estimated_cash_collection',
            })}
            data-testid="funnel-cash-collection"
          />
          <KPICard
            title="Lead to Opp %"
            value={funnelKPIs ? `${funnelKPIs.lead_to_opportunity_rate}%` : '-'}
            subValue={funnelKPIs ? `${funnelKPIs.entered_opportunity_count} of ${funnelKPIs.total_leads}` : ''}
            icon={Percent}
            variant={funnelKPIs ? (funnelKPIs.lead_to_opportunity_rate >= 20 ? 'success' : funnelKPIs.lead_to_opportunity_rate >= 10 ? 'warning' : 'danger') : 'default'}
            loading={loading}
            onInfo={() => openKPI({
              title: 'Lead to Opportunity Conversion',
              value: funnelKPIs ? `${funnelKPIs.lead_to_opportunity_rate}%` : '-',
              formula: '(Leads that entered opportunity ÷ Total Leads) × 100',
              description: 'Share of leads that ever progressed into the opportunity pipeline. ≥20% healthy, 10–19% warning, below 10% danger.',
              inputs: [
                { label: 'Entered opportunity (count)', value: funnelKPIs?.entered_opportunity_count ?? 0 },
                { label: 'Total leads', value: funnelKPIs?.total_leads ?? 0 },
              ],
              source: 'leads.entered_opportunity flag',
            })}
            data-testid="funnel-lead-to-opp"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Stage Funnel - Side by Side Lead vs Opportunity */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Stage Funnel</CardTitle>
              <p className="text-xs text-slate-500">Lead stages vs Opportunity stages</p>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-64 w-full" />
              ) : stageDistribution ? (
                <div className="grid grid-cols-2 gap-4">
                  {/* Lead Stages Column */}
                  <div>
                    <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">Lead Stages</div>
                    <div className="space-y-1.5">
                      {stageDistribution.lead_stages.map(s => {
                        const maxLead = Math.max(...stageDistribution.lead_stages.map(x => x.count), 1);
                        const w = (s.count / maxLead) * 100;
                        return (
                          <button key={s.stage_id} className="w-full text-left hover:bg-slate-50 p-1 rounded transition-colors" onClick={() => navigate(`/leads?stage=${s.stage_id}`)}>
                            <div className="flex items-center justify-between text-[11px] mb-0.5">
                              <span className="text-slate-600 truncate">{s.label}</span>
                              <span className="font-medium ml-1">{s.count}</span>
                            </div>
                            <div className="h-3 bg-slate-100 rounded overflow-hidden">
                              <div className="h-full bg-blue-500 rounded transition-all" style={{ width: `${Math.max(w, 2)}%` }} />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {/* Opportunity Stages Column */}
                  <div>
                    <div className="text-xs font-semibold text-purple-600 uppercase tracking-wider mb-2">Opportunity Stages</div>
                    <div className="space-y-1.5">
                      {stageDistribution.opportunity_stages.map(s => {
                        const maxOpp = Math.max(...stageDistribution.opportunity_stages.map(x => x.count), 1);
                        const w = (s.count / maxOpp) * 100;
                        const barColor = s.stage_id === 'closed_won' ? 'bg-emerald-500' : s.stage_id === 'closed_lost' ? 'bg-red-400' : 'bg-purple-500';
                        return (
                          <button key={s.stage_id} className="w-full text-left hover:bg-slate-50 p-1 rounded transition-colors" onClick={() => navigate(`/leads?stage=${s.stage_id}`)}>
                            <div className="flex items-center justify-between text-[11px] mb-0.5">
                              <span className="text-slate-600 truncate">{s.label}</span>
                              <span className="font-medium ml-1">{s.count}</span>
                            </div>
                            <div className="h-3 bg-slate-100 rounded overflow-hidden">
                              <div className={`h-full ${barColor} rounded transition-all`} style={{ width: `${Math.max(w, 2)}%` }} />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-slate-400">No funnel data</div>
              )}
            </CardContent>
          </Card>

          {/* Stage Distribution Pie Chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Stage Distribution</CardTitle>
              <p className="text-xs text-slate-500">Percentage of leads in each stage</p>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-64 w-full" />
              ) : stageDistribution ? (
                <StageDistributionChart data={stageDistribution} />
              ) : (
                <div className="h-64 flex items-center justify-center text-slate-400">No data</div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Leads by US State Map */}
        <LeadsByStateMap />

        {/* Bottleneck Alerts */}
        {funnelData?.bottleneck_alerts?.length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
                <AlertTriangle className="h-5 w-5" />
                Bottleneck Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {funnelData.bottleneck_alerts.map((alert) => (
                  <div 
                    key={alert.message} 
                    className={`p-3 rounded-lg flex items-center justify-between ${
                      alert.severity === 'high' ? 'bg-red-100' : 'bg-yellow-100'
                    }`}
                  >
                    <span className="text-sm">{alert.message}</span>
                    <Badge variant={alert.severity === 'high' ? 'destructive' : 'secondary'}>
                      {alert.severity}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Rep Scorecard */}
        {funnelData?.rep_scorecard?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5 text-slate-400" />
                Rep Scorecard
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rep</TableHead>
                      <TableHead className="text-right">Leads</TableHead>
                      <TableHead className="text-right">Consults</TableHead>
                      <TableHead className="text-right">Close %</TableHead>
                      <TableHead className="text-right">Revenue</TableHead>
                      <TableHead className="text-right">Receivable</TableHead>
                      <TableHead className="text-right">Task %</TableHead>
                      <TableHead className="text-right">SLA</TableHead>
                      <TableHead className="text-right">Overdue</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {funnelData.rep_scorecard.slice(0, 10).map((rep) => (
                      <TableRow key={rep.rep_id} className="hover:bg-slate-50">
                        <TableCell className="font-medium">{rep.rep_name}</TableCell>
                        <TableCell className="text-right">{rep.leads_assigned}</TableCell>
                        <TableCell className="text-right">{rep.consultations_completed}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant={rep.close_rate >= 25 ? 'default' : rep.close_rate >= 15 ? 'secondary' : 'destructive'}>
                            {rep.close_rate}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right text-green-600 font-medium">
                          ${rep.revenue_collected.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">${rep.revenue_receivable.toLocaleString()}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant={rep.task_completion >= 80 ? 'default' : 'secondary'}>
                            {rep.task_completion}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {rep.sla_breaches > 0 && (
                            <Badge variant="destructive">{rep.sla_breaches}</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {rep.overdue_installments > 0 && (
                            <Badge variant="secondary">{rep.overdue_installments}</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      )}

      {/* ROW 3: Event Intelligence */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-purple-500" />
          Event Intelligence
          <Badge variant="outline" className="ml-2">{eventData?.summary?.total_events || 0} events</Badge>
        </h2>

        {/* Event Summary KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <KPICard
            title="Total Events"
            value={eventData?.summary?.total_events || 0}
            icon={Calendar}
            variant="default"
            loading={loading}
            onClick={() => navigate('/events')}
          />
          <KPICard
            title="Total Attended"
            value={eventData?.summary?.total_attended || 0}
            icon={Users}
            variant="default"
            loading={loading}
          />
          <KPICard
            title="Attendance Rate"
            value={`${eventData?.summary?.overall_attendance_rate || 0}%`}
            icon={Percent}
            variant={eventData?.summary?.overall_attendance_rate >= 70 ? 'success' : 'warning'}
            loading={loading}
          />
          <KPICard
            title="Deals Closed"
            value={eventData?.summary?.total_deals_closed || 0}
            icon={TrendingUp}
            variant="success"
            loading={loading}
          />
          <KPICard
            title="Revenue (30d)"
            value={formatCurrency(eventData?.summary?.total_revenue_collected || 0)}
            icon={DollarSign}
            variant="primary"
            loading={loading}
          />
          <KPICard
            title="$/Attendee"
            value={formatCurrency(eventData?.summary?.avg_revenue_per_attendee || 0)}
            icon={DollarSign}
            variant="default"
            loading={loading}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Event Funnel */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Event Funnel (All Events)</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-48 w-full" />
              ) : eventData?.event_funnel ? (
                <div className="space-y-3">
                  {eventData.event_funnel.map((step, idx) => {
                    const firstCount = eventData.event_funnel[0]?.count || 1;
                    const widthPercent = step.is_currency ? 100 : (step.count / firstCount) * 100;
                    
                    return (
                      <div key={step.stage}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-slate-600">{step.stage}</span>
                          <span className="font-medium">
                            {step.is_currency ? formatCurrency(step.count) : step.count}
                            {!step.is_currency && step.conversion > 0 && (
                              <span className="text-slate-400 ml-1">({step.conversion}%)</span>
                            )}
                          </span>
                        </div>
                        <div className="h-6 bg-slate-100 rounded overflow-hidden">
                          <div 
                            className="h-full bg-purple-500 rounded transition-all"
                            style={{ width: `${widthPercent}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-slate-400">No event data</div>
              )}
            </CardContent>
          </Card>

          {/* Top 10 Events by Revenue */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Top Events by Revenue</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-48 w-full" />
              ) : eventData?.top_10_by_revenue?.length > 0 ? (
                <div className="space-y-2">
                  {eventData.top_10_by_revenue.slice(0, 5).map((event, idx) => {
                    const maxRevenue = eventData.top_10_by_revenue[0]?.revenue_collected || 1;
                    const widthPercent = (event.revenue_collected / maxRevenue) * 100;
                    
                    return (
                      <button
                        key={event.event_id}
                        className="w-full text-left hover:bg-slate-50 p-2 rounded transition-colors"
                        onClick={() => navigate(`/events/${event.event_id}`)}
                      >
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-slate-600 truncate max-w-[60%]">{event.event_name}</span>
                          <span className="font-medium text-green-600">${event.revenue_collected.toLocaleString()}</span>
                        </div>
                        <div className="h-3 bg-slate-100 rounded overflow-hidden">
                          <div 
                            className="h-full bg-green-500 rounded transition-all"
                            style={{ width: `${widthPercent}%` }}
                          />
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                          <span>{event.attended} attended</span>
                          <span>•</span>
                          <span>{event.deals_closed} deals</span>
                          <span>•</span>
                          <span>${event.revenue_per_attendee}/person</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-slate-400">No events with revenue</div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Event Leaderboard Table */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Event Leaderboard</CardTitle>
              <Badge variant="outline">30-day attribution</Badge>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-64 w-full" />
            ) : eventData?.event_leaderboard?.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event</TableHead>
                      <TableHead className="text-right">Registered</TableHead>
                      <TableHead className="text-right">RSVP %</TableHead>
                      <TableHead className="text-right">Attended</TableHead>
                      <TableHead className="text-right">Attend %</TableHead>
                      <TableHead className="text-right">Deals</TableHead>
                      <TableHead className="text-right">Revenue</TableHead>
                      <TableHead className="text-right">$/Attendee</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {eventData.event_leaderboard.slice(0, 10).map((event) => (
                      <TableRow key={event.event_id} className="hover:bg-slate-50">
                        <TableCell>
                          <div>
                            <div className="font-medium">{event.event_name}</div>
                            <div className="text-xs text-slate-500">
                              {event.event_date ? new Date(event.event_date).toLocaleDateString() : 'No date'}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{event.registrations}</TableCell>
                        <TableCell className="text-right">{event.rsvp_rate}%</TableCell>
                        <TableCell className="text-right">{event.attended}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant={event.attendance_rate >= 70 ? 'default' : 'secondary'}>
                            {event.attendance_rate}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">{event.deals_closed}</TableCell>
                        <TableCell className="text-right font-medium text-green-600">
                          ${event.revenue_collected.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">${event.revenue_per_attendee.toLocaleString()}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/events/${event.event_id}`)}>
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="h-32 flex items-center justify-center text-slate-400">No event data available</div>
            )}
          </CardContent>
        </Card>
      </div>
      )}

      {/* ROW 5: Execution Intelligence */}
      {vis('intel_execution_kpis') && (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <CheckSquare className="h-5 w-5 text-orange-500" />
            Execution Intelligence
          </h2>
          <Select value={execRep} onValueChange={setExecRep}>
            <SelectTrigger className="w-40 sm:w-48 h-9 text-xs bg-white border-slate-200 rounded-xl" data-testid="exec-rep-filter">
              <Filter className="h-3.5 w-3.5 mr-1.5 text-slate-400" />
              <SelectValue placeholder="All Reps" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Reps</SelectItem>
              {reps.map((rep) => (
                <SelectItem key={rep.user_id} value={rep.user_id}>{rep.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Execution KPI Cards — 4 cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KPICard
            title="Tasks Due Today"
            value={executionData?.kpi_cards?.tasks_due_today ?? '-'}
            icon={CheckSquare}
            variant="warning"
            loading={loading}
            onClick={() => navigate('/tasks')}
            data-testid="exec-tasks-due-today"
          />
          <KPICard
            title="Overdue Tasks"
            value={executionData?.kpi_cards?.overdue_tasks ?? '-'}
            icon={AlertTriangle}
            variant={executionData?.kpi_cards?.overdue_tasks > 10 ? 'danger' : executionData?.kpi_cards?.overdue_tasks > 5 ? 'warning' : 'default'}
            loading={loading}
            onClick={() => navigate('/tasks?status=overdue')}
            data-testid="exec-overdue-tasks"
          />
          <KPICard
            title="Total Calls Made"
            value={executionData?.kpi_cards?.total_calls ?? '-'}
            subValue="Phone call contacts"
            icon={Phone}
            variant="primary"
            loading={loading}
            data-testid="exec-total-calls"
          />
          <KPICard
            title="Appt. Scheduled"
            value={executionData?.kpi_cards?.appointments_scheduled ?? '-'}
            subValue="Moved to consultation"
            icon={Calendar}
            variant="success"
            loading={loading}
            onClick={() => navigate('/appointments')}
            data-testid="exec-appt-scheduled"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Task Backlog Trend */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Task Backlog Trend (Weekly)</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-48 w-full" />
              ) : executionData?.task_backlog_trend?.length > 0 ? (
                <div className="space-y-3">
                  <div className="flex items-end gap-1 h-32">
                    {executionData.task_backlog_trend.slice(-8).map((week) => {
                      const max = Math.max(...executionData.task_backlog_trend.map(w => Math.max(w.created, w.completed, w.overdue)), 1);
                      return (
                        <div key={week.week_start} className="flex-1 flex flex-col items-center gap-0.5">
                          <div className="w-full flex gap-0.5 items-end h-24">
                            <div 
                              className="flex-1 bg-blue-400 rounded-t"
                              style={{ height: `${(week.created / max) * 100}%` }}
                              title={`Created: ${week.created}`}
                            />
                            <div 
                              className="flex-1 bg-green-500 rounded-t"
                              style={{ height: `${(week.completed / max) * 100}%` }}
                              title={`Completed: ${week.completed}`}
                            />
                            <div 
                              className="flex-1 bg-red-400 rounded-t"
                              style={{ height: `${(week.overdue / max) * 100}%` }}
                              title={`Overdue: ${week.overdue}`}
                            />
                          </div>
                          <span className="text-[10px] text-slate-400">{week.week_label}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-4 text-xs">
                    <div className="flex items-center gap-1"><div className="w-3 h-3 bg-blue-400 rounded" /> Created</div>
                    <div className="flex items-center gap-1"><div className="w-3 h-3 bg-green-500 rounded" /> Completed</div>
                    <div className="flex items-center gap-1"><div className="w-3 h-3 bg-red-400 rounded" /> Overdue</div>
                  </div>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-slate-400">No task data</div>
              )}
            </CardContent>
          </Card>

          {/* SLA Heatmap */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">SLA Heatmap (Rep × Stage)</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-48 w-full" />
              ) : executionData?.sla_heatmap?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr>
                        <th className="text-left p-1">Rep</th>
                        {(executionData.sla_stage_columns || []).map(col => (
                          <th key={col.stage_id} className="p-1 text-center" title={col.label}>
                            {col.label.split(' ').map(w => w[0]?.toUpperCase() || '').join('').slice(0, 3)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {executionData.sla_heatmap.slice(0, 8).map(rep => (
                        <tr key={rep.rep_id}>
                          <td className="p-1 font-medium truncate max-w-[100px]">{rep.rep_name}</td>
                          {(executionData.sla_stage_columns || []).map(col => {
                            const stage = col.stage_id;
                            const stageData = rep.stages?.[stage];
                            const bgColor = stageData?.status === 'red' ? 'bg-red-500' 
                                          : stageData?.status === 'yellow' ? 'bg-yellow-400' 
                                          : 'bg-green-500';
                            return (
                              <td key={stage} className="p-1 text-center">
                                <div 
                                  className={`w-6 h-6 mx-auto rounded ${bgColor} flex items-center justify-center text-white text-[10px] font-bold cursor-pointer`}
                                  title={`${rep.rep_name} - ${col.label}: ${stageData?.stuck_leads || 0} stuck`}
                                  onClick={() => navigate(`/leads?stage=${stage}&assigned_to=${rep.rep_id}`)}
                                >
                                  {stageData?.stuck_leads || 0}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                    <div className="flex items-center gap-1"><div className="w-3 h-3 bg-green-500 rounded" /> Healthy (0)</div>
                    <div className="flex items-center gap-1"><div className="w-3 h-3 bg-yellow-400 rounded" /> Warning (1-2)</div>
                    <div className="flex items-center gap-1"><div className="w-3 h-3 bg-red-500 rounded" /> Critical (3+)</div>
                  </div>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-slate-400">No SLA data</div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* No-Activity Leads */}
        {executionData?.no_activity_leads?.length > 0 && (
          <Card className="border-orange-200 bg-orange-50">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2 text-orange-800">
                  <Clock className="h-5 w-5" />
                  Leads Without Activity ({executionData.kpi_cards?.no_activity_leads || 0})
                </CardTitle>
                <Badge variant="outline" className="text-orange-600 border-orange-300">
                  &gt;{executionData.thresholds?.no_activity_days} days inactive
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Lead</TableHead>
                      <TableHead>Stage</TableHead>
                      <TableHead>Rep</TableHead>
                      <TableHead className="text-right">Days Inactive</TableHead>
                      <TableHead>Last Activity</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {executionData.no_activity_leads.slice(0, 10).map(lead => (
                      <TableRow key={lead.lead_id} className="bg-white">
                        <TableCell>
                          <div className="font-medium">{lead.name}</div>
                          <div className="text-xs text-slate-500">{lead.phone}</div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{lead.stage?.replace(/_/g, ' ')}</Badge>
                        </TableCell>
                        <TableCell>{lead.rep_name}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant="destructive">{lead.days_inactive} days</Badge>
                        </TableCell>
                        <TableCell className="text-xs text-slate-500">
                          {lead.last_activity_date ? new Date(lead.last_activity_date).toLocaleDateString() : 'Never'}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/leads/${lead.lead_id}`)}>
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      )}

      {/* Quick Links */}
      <div className="flex items-center gap-3 pt-4 border-t flex-wrap">
        <span className="text-sm text-slate-500">Quick Links:</span>
        <Button variant="outline" size="sm" onClick={() => navigate('/collections-worklist')}>
          Collections Worklist
        </Button>
        <Button variant="outline" size="sm" onClick={() => navigate('/tasks')}>
          All Tasks
        </Button>
        <Button variant="outline" size="sm" onClick={() => navigate('/funnel')}>
          Full Funnel View
        </Button>
      </div>
      )}


      {/* Close Rate Modal */}
      <Dialog open={showCloseRateModal} onOpenChange={setShowCloseRateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Close Rate Calculator</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="text-center">
              <div className="text-5xl font-bold text-slate-900 mb-2">
                {0}%
              </div>
              <p className="text-slate-500">Close Rate</p>
            </div>
            <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{0}</div>
                <p className="text-sm text-slate-500">Closed Won</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{0}</div>
                <p className="text-sm text-slate-500">Consultation Completed</p>
              </div>
            </div>
            <p className="text-xs text-slate-400 text-center">
              Formula: Closed Won ÷ Consultation Completed × 100
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Overdue Worklist Modal */}
      <Dialog open={showOverdueModal} onOpenChange={setShowOverdueModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Overdue Worklist {selectedBucket && `(${financialData?.aging_buckets?.[selectedBucket]?.label})`}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {overdueWorklist.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>File ID</TableHead>
                    <TableHead>Rep</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead className="text-right">Days Overdue</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {overdueWorklist.map((item) => {
                    const balanceStr = `$${(item.balance_amount || 0).toLocaleString()}`;
                    const dueDateStr = item.due_date ? new Date(item.due_date).toLocaleDateString() : '-';
                    const daysVariant = item.days_overdue > 60 ? 'destructive' : item.days_overdue > 30 ? 'secondary' : 'outline';
                    
                    return (
                      <TableRow key={item.installment_id}>
                        <TableCell>
                          <div className="font-medium">{item.customer_name}</div>
                          <div className="text-xs text-slate-500">{item.customer_phone}</div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{item.file_id}</TableCell>
                        <TableCell>{item.rep_name}</TableCell>
                        <TableCell className="text-right font-semibold text-red-600">{balanceStr}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant={daysVariant}>{item.days_overdue} days</Badge>
                        </TableCell>
                        <TableCell className="text-sm">{dueDateStr}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/contracts/${item.contract_id}`)}>
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <TrendingUp className="h-12 w-12 mx-auto mb-4 text-green-500" />
                <p>No overdue installments. Excellent!</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Stuck Leads Modal */}
      <Dialog open={showStuckLeadsModal} onOpenChange={setShowStuckLeadsModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-500" />
              Stuck Leads ({stuckLeads.length})
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {stuckLeads.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Lead</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Rep</TableHead>
                    <TableHead className="text-right">Days in Stage</TableHead>
                    <TableHead className="text-right">Over Threshold</TableHead>
                    <TableHead>Last Activity</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stuckLeads.slice(0, 50).map((lead) => {
                    const stageDisplay = lead.stage ? lead.stage.replace(/_/g, ' ') : '';
                    const lastTaskDateStr = lead.last_task_date ? new Date(lead.last_task_date).toLocaleDateString() : '';
                    
                    return (
                      <TableRow key={lead.lead_id}>
                        <TableCell>
                          <div className="font-medium">{lead.name}</div>
                          <div className="text-xs text-slate-500">{lead.phone}</div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{stageDisplay}</Badge>
                        </TableCell>
                        <TableCell>{lead.rep_name}</TableCell>
                        <TableCell className="text-right">{lead.days_in_stage} days</TableCell>
                        <TableCell className="text-right">
                          <Badge variant="destructive">+{lead.days_over_threshold}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {lead.last_task ? (
                            <div>
                              <div className="text-slate-600">{lead.last_task}</div>
                              <div className="text-xs text-slate-400">{lastTaskDateStr}</div>
                            </div>
                          ) : (
                            <span className="text-slate-400">No recent activity</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/leads/${lead.lead_id}`)}>
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <TrendingUp className="h-12 w-12 mx-auto mb-4 text-green-500" />
                <p>No stuck leads. Pipeline is flowing!</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Widget Visibility Panel — Owner/Admin only (controls both Dashboard & Intelligence) */}
      <DashboardVisibilityPanel open={visibilityOpen} onOpenChange={setVisibilityOpen} />
    </div>
  );
}
